import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils";
import { BlockLink } from "../../render/context";
import { GlassCard } from "../ui/glass-card";
import { Reveal } from "../ui/reveal";
import { ExternalLinkIcon } from "../ui/external-link-icon";
// Keep the smaller profile available for testing batch boundaries.
export const NEWS_PAGINATION_PROFILES = {
    test: { batchSize: 12, initialVisible: 6, revealCount: 3 },
    production: { batchSize: 96, initialVisible: 12, revealCount: 3 },
};
export const NEWS_PAGINATION = NEWS_PAGINATION_PROFILES.production;
const META = "m-0 flex flex-wrap items-start text-sm font-bold leading-[1.2] tracking-[0.08em] text-foreground uppercase";
const isExternal = (href) => /^https?:\/\//i.test(href);
function ArrowIcon() {
    return (_jsx("svg", { "aria-hidden": "true", className: "h-[14px] w-[16px] shrink-0 fill-none stroke-current stroke-[1.5]", viewBox: "0 0 16 14", children: _jsx("path", { d: "M1 7h13M9 2l5 5-5 5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function NewsCard({ item }) {
    const href = item.href ?? item.readMoreHref;
    const external = href ? isExternal(href) : false;
    const showExternalIcon = item.linkIcon
        ? item.linkIcon === "external"
        : external;
    return (_jsxs(GlassCard, { as: "article", tint: "blue", radius: 15, frost: 4, refraction: 80, depth: 100, dispersion: 50, className: "h-auto min-h-[310px] w-full gap-4 px-7 py-8 @min-[701px]:h-full @min-[701px]:min-h-[310px] @min-[701px]:p-10", children: [_jsxs("p", { className: "type-eyebrow-lg m-0 leading-[1.4] text-foreground", children: [_jsx("span", { className: "block", children: item.category || "Press Release" }), item.date ? (_jsx("span", { className: "block font-medium", children: item.date })) : null] }), _jsxs("div", { className: "flex min-h-0 w-full flex-1 flex-col items-start gap-[22px]", children: [_jsx("h2", { className: cn("m-0 w-full overflow-hidden font-sans text-3xl font-light leading-[1.2] text-pretty text-brand-ink-surface [display:-webkit-box] [-webkit-box-orient:vertical]", 
                        // The summary takes a line off the headline's budget rather than
                        // pushing the card taller than its neighbours in the row.
                        item.summary ? "[-webkit-line-clamp:3]" : "[-webkit-line-clamp:4]"), children: href ? (_jsx(BlockLink, { href: href, target: external ? "_blank" : undefined, className: "focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary", children: item.title })) : (item.title) }), item.summary ? (_jsx("div", { className: "w-full overflow-hidden text-[15px] font-light leading-[1.55] text-brand-ink-surface/80 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3]", children: item.summary })) : null, href ? (_jsxs(BlockLink, { href: href, target: external ? "_blank" : undefined, className: cn(META, "mt-auto inline-flex items-center gap-3 leading-none text-primary", "transition-colors duration-200 ease-out hover:text-brand-blue-dark focus-visible:text-brand-blue-dark [&>svg]:transition-transform [&>svg]:duration-200 [&>svg]:ease-in-out hover:[&>svg]:translate-x-[4px] focus-visible:[&>svg]:translate-x-[4px] motion-reduce:transition-none motion-reduce:[&>svg]:transition-none", "focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-[5px] focus-visible:outline-primary"), children: [_jsx("span", { "aria-hidden": "true", children: "Read more" }), _jsx("span", { className: "sr-only", children: `Read more: ${item.title}` }), showExternalIcon ? _jsx(ExternalLinkIcon, {}) : _jsx(ArrowIcon, {})] })) : null] })] }));
}
/** Responsive press-release grid used by both the CMS block and `/news`. */
function NewsList({ className, items, emptyMessage = "No press releases found.", pagination = false, nextOffset = null, loadMore, ...props }) {
    const [additional, setAdditional] = React.useState([]);
    const [visible, setVisible] = React.useState(NEWS_PAGINATION.initialVisible);
    const [offset, setOffset] = React.useState(nextOffset);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const sentinel = React.useRef(null);
    const inFlight = React.useRef(false);
    const controller = React.useRef(null);
    React.useEffect(() => () => controller.current?.abort(), []);
    React.useEffect(() => {
        controller.current?.abort();
        inFlight.current = false;
        setAdditional([]);
        setVisible(NEWS_PAGINATION.initialVisible);
        setOffset(nextOffset);
        setLoading(false);
        setFailed(false);
    }, [items, nextOffset]);
    const allItems = [...items, ...additional];
    const more = visible < allItems.length || (offset !== null && !!loadMore);
    const advance = React.useCallback(async () => {
        if (inFlight.current)
            return;
        if (visible < allItems.length) {
            setVisible((count) => Math.min(count + NEWS_PAGINATION.revealCount, allItems.length));
            return;
        }
        if (offset === null || !loadMore)
            return;
        inFlight.current = true;
        const abort = new AbortController();
        controller.current = abort;
        setLoading(true);
        setFailed(false);
        try {
            const page = await loadMore(offset, abort.signal);
            if (abort.signal.aborted)
                return;
            setAdditional((current) => [...current, ...page.items]);
            setOffset(page.nextOffset ?? null);
            setVisible((count) => count + Math.min(NEWS_PAGINATION.revealCount, page.items.length));
        }
        catch {
            if (!abort.signal.aborted)
                setFailed(true);
        }
        finally {
            if (!abort.signal.aborted) {
                inFlight.current = false;
                setLoading(false);
            }
        }
    }, [visible, allItems.length, offset, loadMore]);
    React.useEffect(() => {
        if (!pagination ||
            !more ||
            loading ||
            failed ||
            !sentinel.current ||
            typeof IntersectionObserver === "undefined")
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                observer.disconnect();
                void advance();
            }
        }, 
        // The sentinel starts at the grid's bottom. Shrink the viewport so it
        // enters only once the last row is 100px above the screen's bottom.
        { rootMargin: "0px 0px -100px 0px" });
        observer.observe(sentinel.current);
        return () => observer.disconnect();
    }, [pagination, more, loading, failed, advance]);
    return (_jsxs("div", { className: cn("w-full", className), "data-press-releases": true, ...props, children: [items.length === 0 ? (_jsx("p", { className: "py-12 text-center text-base text-muted-foreground", children: emptyMessage })) : (_jsx("div", { className: "mx-auto grid w-full max-w-[1132px] grid-cols-1 items-stretch gap-3 @min-[701px]:grid-cols-2 @min-[1101px]:grid-cols-3", children: (pagination ? allItems.slice(0, visible) : items).map((item, index) => (_jsx(Reveal, { className: "h-full min-w-0", delay: pagination ? 0 : 0.08 + index * 0.05, eager: pagination, style: pagination
                        ? { transform: "none", translate: "none" }
                        : undefined, children: _jsx(NewsCard, { item: item }) }, item.key ?? item.href ?? item.title))) })), pagination && more ? (_jsx("div", { ref: sentinel, className: "flex min-h-16 items-center justify-center", children: loading ? (_jsxs("div", { role: "status", "aria-label": "Loading more articles", children: [_jsx("span", { "aria-hidden": "true", className: "block size-6 animate-spin rounded-full border-2 border-brand-blue/20 border-t-brand-blue motion-reduce:animate-none" }), _jsx("span", { className: "sr-only", children: "Loading more articles\u2026" })] })) : (_jsx("button", { type: "button", onClick: () => void advance(), className: failed ? "text-primary underline" : "sr-only focus:not-sr-only", children: failed
                        ? "Couldn’t load more articles. Try again"
                        : "Load more articles" })) })) : null] }));
}
export { NewsList };
//# sourceMappingURL=news-list.js.map